import { inject, observer } from "mobx-react";
import React from "react";
import   Box  from "react-raster";
import { Trans, useTranslation } from "react-i18next";
import { match } from "react-router";
import { Cell, DivPaper, Heading, MultiSelect, Paper, Spacer, Option } from "src/components";
import { theme } from "src/config";
import { Space } from "src/models";
import { MatchParams } from "src/routes";
import { LogStore, RoutingStore, SpaceStore } from "src/stores";
import { DataType, ItemType, SpaceType } from "src/types";
import styled from "styled-components";
import { DashboardCalendar } from "./calendar";
import { ItemsBoard } from "./itemsBoard";
import { Notifications } from "./newsFeed";
import { NextItems } from "./nextItems";
import { format } from "date-fns";
import { enGB, sv } from "date-fns/locale";

interface IDashboardProps {
  logStore?:     LogStore;
  params:        match<MatchParams>;  // This is injected so it has to be called match
  routingStore?: RoutingStore;
  spaceStore?:   SpaceStore;
}

export const Dashboard = inject("routingStore", "logStore", "spaceStore")(observer((props: IDashboardProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { routingStore, spaceStore } = props;
  const locale = i18n.language == 'sv' ? sv : enGB;

  return (
    <Container offset={routingStore.navBarSize || "0px"}>
      <Row >
        <Column style={{margin: '0 0 1.5rem 0.875rem'}}>
          <Heading>{format(new Date(), 'd MMMM yyyy', {locale})}</Heading>
        </Column>
      </Row>
      <Paper
        style={{ ...theme.paper.fullwidth, marginBottom: "2rem", overflow: "hidden" }}
        $center
        $noShadow
      >
        <Row style={{gap: 20}}>
          {[DataType.Milestone, DataType.Deliverable, DataType.Meeting].map(
            (t: ItemType, i:number) => (
              <Column key={t} style={{width: '33%'}}>
                <NextItems type={t} />
              </Column>
            )
          )}
        </Row>
      </Paper>
      <FilterContainer>
        <StyledMultiselect
          selected = {spaceStore.projectFilter}
          onRemove = {(option: Option<Space>) => spaceStore.changeProjectFilter(option, false)}
          onAdd    = {(option: Option<Space>) => spaceStore.changeProjectFilter(option, true)}
          label    = {t("project")}
          options  = {spaceStore.getUserSpaces(SpaceType.Project).map((p) => ({ label: p.title, value: p }))}
        />
      </FilterContainer>
      <DashboardContainer>
        <NewsfeedContainer>
          <Notifications/>
        </NewsfeedContainer>

        <ItemsContainer>
          <Paper
            $center
            $noShadow
            style={{
              ...theme.paper.fullwidth,
              overflowY: "hidden",
              height:    "auto"
            }}
          >
            <Heading type="h3">{t("my_calendar")}</Heading>
            <DashboardCalendar />
          </Paper>
          <DivPaper
            $noShadow
            style={{ ...theme.paper.fullwidth, marginTop: "2rem", marginBottom: "2rem", overflow: "hidden" }}
          >
            <Heading type="h3">{t("my_questions_deliveries")}</Heading>
            <Spacer size="medium" />
            <ItemsBoard />
          </DivPaper>
        </ItemsContainer>
      </DashboardContainer>
    </Container>
  );
}));

const DashboardContainer = styled.div`
  display:         flex;
  flex-direction:  row;
  justify-content: space-between;
  margin-bottom:   2rem;
`;

const NewsfeedContainer = styled.div`
  width: 49%;
`;

const ItemsContainer = styled.div`
  width: 49%; 
`

const Container = styled.div<{ offset: string }>`
  height: 100%;
  margin: 2rem;
  overflow: auto;
  overflow-x: hidden;
`;

const Column = styled(Cell) <{ noGap?: boolean }>`
  display:        flex;
  flex-direction: column;
  gap:            ${(p) => (p.noGap ? "0" : "2rem")};
`;

const Row = styled(Cell) <{ spaceBetween?: boolean }>`
  display:         flex;
  height:          fit-content;
  flex-direction:  row;
  justify-content: ${(p) => p.spaceBetween && "space-between"};
  list-style-type: none;
  gap:             1.5rem;
`;

const StyledMultiselect = styled(MultiSelect)`
  width:      30rem;
  z-index:    10;
  background: white;
`;

const FilterContainer = styled.div`
  display:         flex;
  justify-content: flex-end;
  padding:         2rem;
`;